import useAppStore from "@hooks/useAppStore";
import i18n, { supportedLanguages } from "@i18n/i18n";
import CloseIcon from "@mui/icons-material/Close";
import LogoutIcon from "@mui/icons-material/Logout";
import { Tooltip } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { observer } from "mobx-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

const AppSettingsDrawer = observer(() => {
  const appStore = useAppStore();
  const navigate = useNavigate();
  const [language, setLanguage] = useState(supportedLanguages.find((l) => l.locales.includes(appStore.language)));

  const handleLangaugeChange = (_event: any, value: any) => {
    setLanguage(value);
    appStore.setLanguage(value.locale);
    i18n.changeLanguage(value.locale.toLowerCase());
  };

  const handleLogout = () => {
    navigate("/logout");
  };

  return (
    <Drawer anchor="right" open={appStore.openSettings} onClose={appStore.toggleOpenSettings}>
      <Card variant="outlined" sx={{ width: "350px", height: "100%" }}>
        <CardContent>
          <Box>
            <Stack spacing={1}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Typography variant="h5">{i18n.t("settings.header")}</Typography>
                <IconButton onClick={appStore.toggleOpenSettings}>
                  <CloseIcon />
                </IconButton>
              </Box>

              <Divider />

              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Typography variant="h6">{i18n.t("settings.title.dark-mode")}</Typography>
                <Switch checked={appStore.colorMode === "dark"} onChange={appStore.toggleColorMode} />
              </Box>

              <Divider />

              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Typography variant="h6">{i18n.t("settings.title.load-models")}</Typography>
                <Switch checked={appStore.isModelEnabled} onChange={appStore.toggleIsModelEnabled} />
              </Box>

              <Divider />

              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Typography variant="h6">{i18n.t("settings.title.show-project-stepper")}</Typography>
                <Switch checked={appStore.isProjectStepperEnabled} onChange={appStore.toggleIsProjectStepperEnabled} />
              </Box>

              <Divider />

              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Typography variant="h6">{i18n.t("settings.title.enable-logging")}</Typography>
                <Switch checked={appStore.isLoggingEnabled} onChange={appStore.toggleIsLoggingEnabled} />
              </Box>

              <Divider />

              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Typography variant="h6">{i18n.t("settings.title.enable-profiling")}</Typography>
                <Switch checked={appStore.isProfilingEnabled} onChange={appStore.toggleIsProfilingEnabled} />
              </Box>

              <Divider />

              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Typography variant="h6">{i18n.t("settings.title.language")}</Typography>
                <Autocomplete
                  options={supportedLanguages}
                  value={language}
                  getOptionLabel={(option) => option.label}
                  renderInput={(params) => (
                    <TextField
                      variant={"outlined"}
                      {...params}
                      size={"small"}
                      slotProps={{
                        inputLabel: {
                          shrink: false,
                        },
                      }}
                    />
                  )}
                  onChange={handleLangaugeChange}
                  disableClearable
                  autoHighlight
                  sx={{ minWidth: "150px" }}
                />
              </Box>

              <Divider />

              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Tooltip title={i18n.t("settings.title.sign-out")}>
                  <IconButton onClick={handleLogout} color="inherit">
                    <LogoutIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Stack>
          </Box>
        </CardContent>
      </Card>
    </Drawer>
  );
});

export default AppSettingsDrawer;
